/**
 * 账号类面板共用的外壳：遮罩 + 底部抽屉 + 标题。
 * 绑邮箱、邮箱登录、发链接信、兑换链接、选头像、历史牌局都用它。
 *
 * 复用 `.sheet-scrim`（theme/global.css）与 `.sheet-panel` / `.sheet-title`（lobby.css）——
 * 全应用的浮层长一个样，这里不另起一套。
 *
 * ★ `dismissible={false}` 是签发那一屏的核心：码只显示这一次，
 *   点遮罩、按 Esc 都不能把它关掉，必须点「我已经保存好了」。
 *   误关一次 = 用户永远拿不回这串码，而他以为自己已经有了。
 */
import { useId } from 'react'
import type { ReactNode } from 'react'
import { createPortal } from 'react-dom'

import { useModalDialog } from '../hooks/useModalDialog.js'
import { IconClose } from '../icons/index.js'
import './account.css'
import '../lobby/lobby.css'

export interface SheetShellProps {
  /** 无障碍名字（也是可见标题） */
  readonly title: string
  readonly icon?: ReactNode
  /** false = 只能靠面板里的按钮关（遮罩与 Esc 全部失效） */
  readonly dismissible?: boolean
  onClose?(): void
  readonly children: ReactNode
}

export function SheetShell({ title, icon, dismissible = true, onClose, children }: SheetShellProps): ReactNode {
  const titleId = useId()
  const { dialogRef, onDialogKeyDown } = useModalDialog({ open: true, onClose, dismissible })

  const panel = (
    <div
      className="sheet-scrim sheet-scrim--paper"
      role="presentation"
      // 摆到 DOM 上，好让「码在屏幕上时关不掉」这条能被渲染测试钉死
      data-dismissible={dismissible ? 'yes' : 'no'}
      onClick={() => (dismissible ? onClose?.() : undefined)}
    >
      {/* 面板自己吞掉点击：只有点遮罩才关 */}
      <div
        className="sheet-panel"
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        onKeyDown={onDialogKeyDown}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sheet-head">
          <h2 className="sheet-title acct-title" id={titleId}>
            {icon}
            {title}
          </h2>
          {dismissible && onClose !== undefined && (
            <button type="button" className="sheet-close pressable" onClick={onClose} aria-label="关闭">
              <IconClose size={18} />
            </button>
          )}
        </div>
        <div className="acct-panel">{children}</div>
      </div>
    </div>
  )

  // 舞台开了 `contain: layout paint`，fixed 遮罩会被裁进舞台里，所以挂到 body 上；
  // 没有 document 的环境原地渲染，测试照样能断言内容
  const doc = (globalThis as { document?: Document }).document
  return doc?.body == null ? panel : createPortal(panel, doc.body)
}
